import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  Box,
  Typography,
  TableContainer,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHead,
  CircularProgress,
  IconButton,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

import DayEndButtons from "./DayEndButtons";
import { DayEndLayout } from "./DayEndLayout";
import CustomDateTimePicker from "../../../components/atoms/custom-date-time-picker/custom-date-time-picker";
import RegisterBalancingTable, {
  IModifiedTableData,
} from "./RegisterBalancingTable";
import { Colors } from "../../../configs";
import { formatDate, pxToRem } from "../../../util";
import dayjs from "dayjs";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { useSnackbarContext } from "../../../providers/SnackbarProvider";
import {
  endDayAction,
  getAllDayEndStationSummaryAction,
  getStoreStatusAction,
} from "../../../redux/actions/cashierStationActions";
import {
  IDayEndRequest,
  IStationBalancing,
} from "../../../typings/interfaces/requests/cashierStationRequests";
import { useNavigate } from "react-router-dom";
import { ICashierPoint } from "../../../typings";
import { RegisterNoteModal } from "./RegisterNoteModal";
import { clearEndDayResponse } from "../../../redux/slice/cashierStationSlice";

interface IUpdatedData extends IModifiedTableData {
  imbalanceReason: string;
}

const RegisterBalancingPage: React.FC = () => {
  const [isRegisterBalancingClicked, setIsRegisterBalancingClicked] =
    useState(false);
  const [date, setDate] = useState<Date>(new Date());
  const [updatedData, setUpdatedData] = useState<IUpdatedData[]>([]);
  const [registerNoteModalOpen, setRegisterNoteModalOpen] = useState(false);
  const [originalData, setOriginalData] = useState<IModifiedTableData[]>([]);
  const [dateIndex, setDateIndex] = useState(0);
  const [currentStationForNote, setCurrentStationForNote] = useState<number | null>(null);
  const [pendingStationIndex, setPendingStationIndex] = useState<number>(0);
  const hasShownSuccessMessage = useRef(false);
  const hasShownErrorMessage = useRef<string | null>(null);
  const hasShownSummaryErrorMessage = useRef<string | null>(null);

  const dispatch = useAppDispatch();
  const snackbar = useSnackbarContext();
  const navigate = useNavigate();
  const {
    dayEndStationSummaryError,
    dayEndStationSummaryResponse,
    dayEndStationSummaryLoading,
    endDayError,
    endDayResponse,
    activeStation,
    getStoreStatusResponse,
  } = useAppSelector((state) => state.cashierStation);

  useEffect(() => {
    const dates = getStoreStatusResponse?.pendingDayEndDates ?? [];
    if (dates.length > 0) {
      setDateIndex(0);
      setDate(new Date(dates[0]));
    }
  }, [getStoreStatusResponse?.pendingDayEndDates]);

  const handlePrevDate = () => {
    if (getStoreStatusResponse?.pendingDayEndDates && dateIndex > 0) {
      const newIndex = dateIndex - 1;
      setDateIndex(newIndex);
      setDate(new Date(getStoreStatusResponse.pendingDayEndDates[newIndex]));
    }
  };

  const handleNextDate = () => {
    if (
      getStoreStatusResponse?.pendingDayEndDates &&
      dateIndex < getStoreStatusResponse.pendingDayEndDates.length - 1
    ) {
      const newIndex = dateIndex + 1;
      setDateIndex(newIndex);
      setDate(new Date(getStoreStatusResponse.pendingDayEndDates[newIndex]));
    }
  };

  const commonCellStyles = {
    textAlign: "center",
    verticalAlign: "middle",
    padding: "0.5rem",
    height: "3.714285714285714rem",
  };

  const tableStyles = {
    cell: commonCellStyles,
    headerCell: {
      color: Colors.WHITE,
      backgroundColor: Colors.SECONDARY,
      ...commonCellStyles,
    },
    bodyCell: {
      color: Colors.MAIN_GRAY,
      backgroundColor: Colors.WHITE,
      ...commonCellStyles,
    },
    totalRowCell: {
      color: Colors.WHITE,
      backgroundColor: Colors.MAIN_GRAY,
      ...commonCellStyles,
    },
  };

  const processNextStationWithImbalance = (startIndex: number = 0) => {
    for (let i = startIndex; i < updatedData.length; i++) {
      const data = updatedData[i];
      if (data.imbalanceReason === "" && data.difference !== 0) {
        setCurrentStationForNote(data.station);
        setPendingStationIndex(i);
        setRegisterNoteModalOpen(true);
        return true;
      }
    }
    return false;
  };

  const handleNoteSubmit = (reason: string) => {
    if (currentStationForNote !== null) {
      setUpdatedData(prev =>
        prev.map(data =>
          data.station === currentStationForNote
            ? { ...data, imbalanceReason: reason }
            : data
        )
      );

      setRegisterNoteModalOpen(false);
      setCurrentStationForNote(null);

      setTimeout(() => {
        const hasMoreStations = processNextStationWithImbalance(pendingStationIndex + 1);

        if (!hasMoreStations) {
          handleActualSave();
        }
      }, 100);
    }
  };

  const handleActualSave = async () => {
    const parsedActiveCashier: ICashierPoint = JSON.parse(
      localStorage.getItem("cashier") ?? "null"
    );

    if (parsedActiveCashier) {
      const preparedBalancing: IStationBalancing[] = updatedData.map(data => ({
        cashierPointId: data.station,
        cashPaymentsTotal: data.cash,
        chequePaymentsTotal: data.check,
        cardPaymentsTotal: data.creditCard,
        inbalanceReason: data.imbalanceReason,
      }));

      if (preparedBalancing.length === 0) {
        snackbar.showSnackbar(
          "No balancing data to save. Please make changes to the register data.",
          "error"
        );
        return;
      }

      // Format date as ISO string (yyyy-MM-dd) for backend
      const formattedDate = dayjs(date).format("YYYY-MM-DD");

      const preparedRequest: IDayEndRequest = {
        storeId: 1,
        isDayFinished: true,
        balancing: preparedBalancing,
        createdDate: formattedDate,
      };

      await dispatch(endDayAction(preparedRequest));
    } else {
      snackbar.showSnackbar("Active cashier not found", "error");
    }
  };

  useEffect(() => {
    dispatch(getAllDayEndStationSummaryAction({ storeId: 1, date }));
  }, [date, dispatch]);

  useEffect(() => {
    if (dayEndStationSummaryError && hasShownSummaryErrorMessage.current !== dayEndStationSummaryError) {
      hasShownSummaryErrorMessage.current = dayEndStationSummaryError;
      snackbar.showSnackbar(dayEndStationSummaryError, "error");
    }
  }, [dayEndStationSummaryError, snackbar]);

  const preparedTable1Data = useMemo(() => {
    if (!dayEndStationSummaryResponse) return undefined;
    return dayEndStationSummaryResponse.map((station) => ({
      station: station.cashierPointId,
      openingBalance: station.openingTotal,
      cash: station.cashPaymentsTotal,
      check: station.chequePaymentsTotal,
      creditCard: station.cardPaymentsTotal,
      monthlyCharge: station.monthlyPaymentTotal,
      discounts: station.discounts,
      sales: station.totalSales,
      registerTotal: station.registerTotal,
      difference: 0,
    }));
  }, [dayEndStationSummaryResponse]);

  const totalRowData = useMemo(() => {
    const response = dayEndStationSummaryResponse ?? [];
    return {
      station: "Total",
      openingBalance: response.reduce(
        (acc, curr) => acc + curr.openingTotal,
        0
      ),
      cash: response.reduce(
        (acc, curr) => acc + curr.cashPaymentsTotal,
        0
      ),
      check: response.reduce(
        (acc, curr) => acc + curr.chequePaymentsTotal,
        0
      ),
      creditCard: response.reduce(
        (acc, curr) => acc + curr.cardPaymentsTotal,
        0
      ),
      monthlyCharge: response.reduce(
        (acc, curr) => acc + curr.monthlyPaymentTotal,
        0
      ),
      discounts: response.reduce(
        (acc, curr) => acc + curr.discounts,
        0
      ),
      sales: response.reduce(
        (acc, curr) => acc + curr.totalSales,
        0
      ),
      registerTotal: response.reduce(
        (acc, curr) => acc + curr.registerTotal,
        0
      ),
    };
  }, [dayEndStationSummaryResponse]);

  const handleSave = async () => {
    // Check if editing has been enabled
    if (!isRegisterBalancingClicked) {
      snackbar.showSnackbar(
        "Please click 'Balance Register' first to enable editing before saving.",
        "error"
      );
      return;
    }

    // If no changes were made, use all original data
    const dataToSave = updatedData.length > 0
      ? updatedData
      : (preparedTable1Data ?? []).map(data => ({
        ...data,
        imbalanceReason: "",
      }));

    // Validate negative differences
    const dataToValidate = updatedData.length > 0 ? updatedData : (preparedTable1Data ?? []);
    const hasNegativeDifference = dataToValidate.some(
      (data) => data.difference < 0
    );
    if (hasNegativeDifference) {
      snackbar.showSnackbar(
        "Cannot proceed: One or more stations have a negative difference.",
        "error"
      );
      return;
    }

    const parsedActiveCashier: ICashierPoint = JSON.parse(
      localStorage.getItem("cashier") ?? "null"
    );

    if (parsedActiveCashier) {
      const hasStationNeedingReason = dataToSave.some(
        (data) => data.imbalanceReason === "" && data.difference !== 0
      );

      if (hasStationNeedingReason) {
        // Only process imbalances from updatedData, not all data
        if (updatedData.length > 0) {
          processNextStationWithImbalance(0);
        } else {
          // If no changes were made and there are imbalances, show error
          snackbar.showSnackbar(
            "Some stations have imbalances. Please adjust the values or add reasons for the imbalances.",
            "error"
          );
        }
        return;
      }

      const preparedBalancing: IStationBalancing[] = dataToSave.map(data => ({
        cashierPointId: data.station,
        cashPaymentsTotal: data.cash,
        chequePaymentsTotal: data.check,
        cardPaymentsTotal: data.creditCard,
        inbalanceReason: data.imbalanceReason,
      }));

      if (preparedBalancing.length === 0) {
        snackbar.showSnackbar(
          "No balancing data available. Please refresh the page and try again.",
          "error"
        );
        return;
      }

      // Format date as ISO string (yyyy-MM-dd) for backend
      const formattedDate = dayjs(date).format("YYYY-MM-DD");

      const preparedRequest: IDayEndRequest = {
        storeId: 1,
        isDayFinished: true,
        balancing: preparedBalancing,
        createdDate: formattedDate,
      };

      await dispatch(endDayAction(preparedRequest));
    } else {
      snackbar.showSnackbar("Active cashier not found", "error");
    }
  };

  useEffect(() => {
    if (endDayError && hasShownErrorMessage.current !== endDayError) {
      hasShownErrorMessage.current = endDayError;
      snackbar.showSnackbar(endDayError, "error");
      // Clear error after showing to prevent loops
      setTimeout(() => {
        dispatch(clearEndDayResponse());
        hasShownErrorMessage.current = null;
      }, 100);
    }
  }, [endDayError, snackbar, dispatch]);

  useEffect(() => {
    if (endDayResponse && !hasShownSuccessMessage.current) {
      // Format the current date as ISO string (yyyy-MM-dd) to compare with backend response
      const currentDateFormatted = dayjs(date).format("YYYY-MM-DD");

      // Check if the response matches the current date being processed
      // Backend returns date in ISO format, so we compare formatted strings
      const responseDateFormatted = endDayResponse.createdDate
        ? dayjs(endDayResponse.createdDate).format("YYYY-MM-DD")
        : null;

      if (responseDateFormatted === currentDateFormatted || endDayResponse.isDayFinished) {
        hasShownSuccessMessage.current = true;
        snackbar.showSnackbar("Day ended successfully", "success");

        // Clear response immediately to prevent re-triggering
        dispatch(clearEndDayResponse());

        // Refetch store status to update pending day-end dates
        // This ensures the Day End button disable logic works without page refresh
        dispatch(getStoreStatusAction(1));

        // Navigate after a short delay to allow user to see the success message
        setTimeout(() => {
          navigate("/pos/lobby");
        }, 1500);
      }
    }
  }, [endDayResponse, date, snackbar, navigate, dispatch]);

  // Reset success message flag when response is cleared
  useEffect(() => {
    if (!endDayResponse) {
      hasShownSuccessMessage.current = false;
    }
  }, [endDayResponse]);

  useEffect(() => {
    if (preparedTable1Data && preparedTable1Data.length > 0) {
      setOriginalData(preparedTable1Data);
    }
  }, [preparedTable1Data]);

  return (
    <DayEndLayout>
      <Box
        sx={{
          backgroundColor: Colors.BACKSHADE_GRAY,
          margin: "2rem",
          width: "120.7142857142857rem",
          height: "52.92857142857143rem",
          display: "flex",
          flexDirection: "row",
          gap: "2rem",
          borderRadius: " 0.7142857142857143rem",
          padding: "2rem",
          marginTop: "0.2rem",
        }}
      >
        {/* Left Section */}
        <Box sx={{ flex: 3, display: "flex", flexDirection: "column" }}>
          <Box
            sx={{
              padding: "1rem",
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography
              sx={{
                color: Colors.MAIN_GRAY,
                fontSize: "2.285714285714286rem",
                fontWeight: "bold",
                marginBottom: "1rem",
              }}
            >
              Register Balancing - {formatDate(date)}
            </Typography>

            {/* <Box
              sx={{
                display: "flex",
              }}
            >
              <CustomDateTimePicker
                type="date"
                value={dayjs(date)}
                onChange={(date) => {
                  if (date) {
                    setDate(date.toDate());
                  } else {
                    setDate(new Date());
                  }
                }}
                enableSelect="past"
                sx={{
                  width: "19.14285714285714rem",
                  height: "3rem",
                }}
              />
            </Box> */}

            <Box
              sx={{
                display: "flex",
                gap: "0.6rem",
                alignItems: "center",
              }}
            >
              <IconButton
                sx={{
                  backgroundColor: "#f0f0f0",
                  borderRadius: "50%",
                  width: "4.5rem",
                  height: "4.5rem",
                  "&:hover": {
                    backgroundColor: "#e0e0e0",
                  },
                }}
                onClick={handlePrevDate}
                disabled={dateIndex === 0}
              >
                <ArrowBackIosNewIcon fontSize="medium" />
              </IconButton>

              <IconButton
                sx={{
                  backgroundColor: "#f0f0f0",
                  borderRadius: "50%",
                  width: "4.5rem",
                  height: "4.5rem",
                  "&:hover": {
                    backgroundColor: "#e0e0e0",
                  },
                }}
                onClick={handleNextDate}
                disabled={
                  dateIndex ===
                  (getStoreStatusResponse?.pendingDayEndDates?.length ?? 0) - 1
                }
              >
                <ArrowForwardIosIcon fontSize="medium" />
              </IconButton>
            </Box>
          </Box>

          <Box>
            <Box
              sx={{
                overflowY: "auto",
                maxHeight: pxToRem(600),
                // make scrollbar beautiful
                "&::-webkit-scrollbar": {
                  width: "0.357rem",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: Colors.ACCENT_GRAY,
                  borderRadius: "0.357rem",
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: Colors.BACKSHADE_GRAY,
                  borderRadius: "0.357rem",
                },
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  overflowX: "auto",
                }}
              >
                <TableContainer
                  sx={{
                    overflow: "auto",
                    borderRadius: "0.714rem",
                  }}
                >
                  <Table
                    sx={{
                      borderCollapse: "separate",
                      borderSpacing: "0.2rem",
                      width: "100%",
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        {[
                          "Station",
                          "Opening Balance",
                          "Cash",
                          "Check",
                          "Credit Card",
                          "Monthly Charge",
                          "Discounts",
                          "Sales",
                          "Register Total",
                        ].map((header, index) => (
                          <TableCell
                            key={index}
                            sx={{
                              ...tableStyles.headerCell,
                              ...(index === 0
                                ? { borderRadius: "0.357rem 0 0 0" }
                                : index === 8
                                  ? { borderRadius: "0 0.357rem 0 0" }
                                  : {}),
                            }}
                          >
                            {header}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {dayEndStationSummaryLoading ? (
                        <TableRow>
                          <TableCell colSpan={9} sx={{ textAlign: "center" }}>
                            <CircularProgress />
                          </TableCell>
                        </TableRow>
                      ) : null}
                      {preparedTable1Data?.map((row, index) => (
                        <TableRow key={index}>
                          {Object.entries(row)
                            .filter(([key]) => key !== "difference")
                            .map(([key, value], colIndex) => (
                              <TableCell
                                key={colIndex}
                                sx={tableStyles.bodyCell}
                              >
                                {typeof value === "number" && colIndex > 0
                                  ? value.toFixed(2)
                                  : value}
                              </TableCell>
                            ))}
                        </TableRow>
                      ))}

                      <TableRow>
                        {Object.values(totalRowData).map((value, index) => (
                          <TableCell
                            key={index}
                            sx={{
                              ...tableStyles.totalRowCell,
                              ...(index === 0
                                ? { borderRadius: "0 0 0 0.357rem" }
                                : index === 8
                                  ? { borderRadius: "0 0 0.357rem 0" }
                                  : {}),
                            }}
                          >
                            {value == "Total"
                              ? value
                              : Number(value).toFixed(2)}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>

                <Box>
                  <Typography
                    sx={{
                      color: Colors.MAIN_GRAY,
                      fontSize: "1.714285714285714rem",
                      fontWeight: "bold",
                      marginBottom: "-2rem",
                      marginTop: "2rem",
                    }}
                  >
                    Enter the Count
                  </Typography>
                </Box>

                <Box
                  sx={{
                    marginTop: "2rem",
                    width: "100%",
                    overflowX: "auto",
                  }}
                >
                  <RegisterBalancingTable
                    data={preparedTable1Data ?? []}
                    editableFields={["cash", "check"]}
                    isEditingEnabled={isRegisterBalancingClicked}
                    onChangeValues={(newData) => {
                      const filtered = newData.filter((newRow) => {
                        const original = originalData.find(
                          (o) => o.station === newRow.station
                        );
                        return (
                          !original ||
                          original.cash !== newRow.cash ||
                          original.check !== newRow.check ||
                          original.creditCard !== newRow.creditCard
                        );
                      });

                      setUpdatedData(
                        filtered.map((item) => ({
                          ...item,
                          imbalanceReason: "",
                        }))
                      );
                    }}
                    isLoading={dayEndStationSummaryLoading}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Right Section */}
        <Box sx={{ flex: 1 }}>
          <DayEndButtons
            isRegisterBalancingPage={true}
            onRegisterBalancingClick={() => setIsRegisterBalancingClicked(true)}
            onSaveClick={handleSave}
          />
        </Box>
      </Box>
      <RegisterNoteModal
        open={registerNoteModalOpen}
        onClose={() => {
          setRegisterNoteModalOpen(false);
          setCurrentStationForNote(null);
        }}
        onAddReason={handleNoteSubmit}
        station={currentStationForNote || undefined}
      />
    </DayEndLayout>
  );
};

export default RegisterBalancingPage;
function handleActualSave() {
  throw new Error("Function not implemented.");
}

